<script setup>
import { ref, computed } from 'vue';

import use$T from 'cfg/useTranslation';
const $t = use$T();
import errors from 'cfg/errorBag';

import Quiz from '@Quiz/Quiz';

import { useAppStore } from 'store/store';
const { messengers } = useAppStore();


const props = defineProps({
  start : { type: String, default: '' },
});

const stepFields = {
  role       : 'role'       ,
  goal       : 'goal'       ,
  vertical   : 'vertical'   ,
  budget     : 'budget'     ,
  experience : 'experience' ,
  sitetype   : 'sitetype'   ,
  visitors   : 'visitors'   ,
  geo        : 'geo'        ,
  current    : 'current'    ,
};

const stepOptions = {
  role       : ['advertiser'  , 'publisher']   ,
  goal       : ['conversions' , 'volume'       , 'reach']       ,
  vertical   : ['sweepstakes' , 'dating'       , 'crypto'       , 'nutra'       , 'mobile' , 'ecommerce' , 'streaming' , 'igaming'] ,
  budget     : ['small'       , 'medium'       , 'large'        , 'enterprise'] ,
  experience : ['new'         , 'intermediate' , 'experienced'] ,
  sitetype   : ['content'     , 'streaming'    , 'downloads'    , 'gaming'      , 'dating' , 'other']    ,
  visitors   : ['small'       , 'medium'       , 'large'        , 'huge']       ,
  geo        : ['tier1'       , 'tier2'        , 'tier3'        , 'mix']        ,
  current    : ['adsense'     , 'othernetwork' , 'direct'       , 'none']       ,
};

const compactSteps = ['vertical', 'sitetype'];

const advertiserFlow = ['role' , 'goal'     , 'vertical' , 'budget' , 'experience' , 'result'];
const publisherFlow  = ['role' , 'sitetype' , 'visitors' , 'geo'    , 'current'    , 'result'];

const state          = ref({});
const currentFlow    = ref(['role']);
const currentIdx     = ref(0);
const historyStack   = ref([]);


const contactNetwork = ref(messengers[0]?.id || '');
const contactId      = ref('');
const submitting     = ref(false);
const sent           = ref(false);

function err(field) {
  return errors.first(field) || '';
}

const currentStepName = computed(()=> currentFlow.value[currentIdx.value] ?? 'role');
const isResult        = computed(()=> currentStepName.value === 'result');
const totalSteps      = computed(()=> currentFlow.value.length - 1);
const stepNum         = computed(()=> Math.min(currentFlow.value.indexOf(currentStepName.value) + 1, totalSteps.value));
const role            = computed(() => state.value.role || 'advertiser');
const progressPct     = computed(()=> {
  if (isResult.value) return 100;
  const idx = currentFlow.value.indexOf(currentStepName.value);
  return ((idx + 1) / (totalSteps.value + 1)) * 100;
});

const activeStep = computed(() => {
  const name = currentStepName.value;
  if (!stepFields[name]) return null;
  return {
    name,
    field   : stepFields[name],
    compact : compactSteps.includes(name),
    title   : $t(`@steps.${name}.title`),
    sub     : $t(`@steps.${name}.sub`),
    options : stepOptions[name].map((value) => ({
      value,
      title : $t(`@steps.${name}.options.${value}.title`),
      sub   : $t(`@steps.${name}.options.${value}.sub`),
    })),
  };
});

function select(field, value) {
  state.value = { ...state.value, [field]: value };

  if (field === 'role') {
    currentFlow.value = value === 'publisher' ? publisherFlow : advertiserFlow;
  }

  historyStack.value.push(currentStepName.value);
  currentIdx.value++;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function back() {
  if (!historyStack.value.length) return;
  historyStack.value.pop();
  currentIdx.value--;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

const result = computed(() => {
  const s = state.value;
  if (s.role === 'publisher') return publisherResult(s);
  return advertiserResult(s);
});

function publisherResult(s) {
  const visitorsRange = {
    small  : { min: 5000, max: 10000 },
    medium : { min: 30000, max: 100000 },
    large  : { min: 250000, max: 1000000 },
    huge   : { min: 1500000, max: 5000000 },
  }[s.visitors] || { min: 30000, max: 100000 };

  const ecpm = {
    tier1: { push: 6.5, native: 4.8, banner: 1.9 },
    tier2: { push: 1.9, native: 1.6, banner: 0.7 },
    tier3: { push: 0.85, native: 0.75, banner: 0.28 },
    mix: { push: 2.4, native: 1.9, banner: 0.85 },
  }[s.geo] || { push: 2.4, native: 1.9, banner: 0.85 };

  const formatBySite = { content: 'push', streaming: 'push', downloads: 'push', gaming: 'native', dating: 'push', other: 'native' };
  const bestFmt = formatBySite[s.sitetype] || 'push';

  const avgImpressions = ((visitorsRange.min + visitorsRange.max) / 2) * 0.6;
  const low            = '$' + Math.round((avgImpressions * ecpm[bestFmt]) / 1000 * 0.7).toLocaleString();
  const high           = '$' + Math.round((avgImpressions * ecpm[bestFmt]) / 1000 * 1.3).toLocaleString();

  return {
    title: $t('@result.pub.title', { low, high }),
    cells: [
      { label: $t('@result.pub.best_format'), value: $t(`@result.formats.${bestFmt}`) },
      { label: $t('@result.pub.avg_ecpm'), value: `$${ecpm[bestFmt].toFixed(2)}`, small: $t('@result.pub.per_1k') },
      { label: $t('@result.pub.payment'), value: $t('@result.pub.payment_value'), small: $t('@result.pub.payment_note') },
      { label: $t('@result.pub.site_approval'), value: $t('@result.pub.approval_value') },
    ],
    highlightLabel: $t('@result.pub.highlight_label'),
    highlightValue: `${low} – ${high}`,
    highlightUnit: $t('@result.pub.highlight_unit'),
    bonus: $t('@result.pub.bonus'),
  };
}

function advertiserResult(s) {
  const fv = {
    sweepstakes: { fmtLbl: 'Classic push', topGeos: 'BR, MX, IN, ID, PH' },
    dating: { fmtLbl: 'Push + native blend', topGeos: 'US, UK, DE, FR, BR' },
    crypto: { fmtLbl: 'Native ads', topGeos: 'US, UK, DE, BR, AR' },
    nutra: { fmtLbl: 'Native ads', topGeos: 'US, UK, CA, AU, DE' },
    mobile: { fmtLbl: 'Push + interstitial', topGeos: 'IN, ID, BR, MX, PH' },
    ecommerce: { fmtLbl: 'Native ads', topGeos: 'US, UK, DE, FR, IT' },
    streaming: { fmtLbl: 'Push + popunder', topGeos: 'US, DE, FR, BR, IN' },
    igaming: { fmtLbl: 'Push + native', topGeos: 'UK, DE, NL, ES, CA' },
  }[s.vertical] || { fmtLbl: 'Classic push', topGeos: 'Mixed' };

  const pricing = { conversions: 'TargetCPA', volume: 'CPC', reach: 'CPM' }[s.goal] || 'TargetCPA';
  const daily = { small: '~$15/day', medium: '~$60/day', large: '~$200/day', enterprise: '~$500+/day' }[s.budget] || '~$60/day';
  const cpa = {
    sweepstakes: '$0.80 – $2.50', dating: '$2 – $8', crypto: '$50 – $200', nutra: '$15 – $40',
    mobile: '$0.50 – $2', ecommerce: '$5 – $25', streaming: '$3 – $12', igaming: '$80 – $250',
  }[s.vertical] || '$2 – $10';
  const vertical = $t(`@result.verticals.${s.vertical || 'default'}`);

  let bonus;
  if (s.experience === 'new') {
    bonus = $t('@result.adv.bonus_new');
  } else if (s.budget === 'large' || s.budget === 'enterprise') {
    bonus = $t('@result.adv.bonus_spend');
  } else {
    bonus = $t('@result.adv.bonus_default');
  }

  return {
    title: $t('@result.adv.title', { vertical }),
    cells: [
      { label: $t('@result.adv.recommended_format'), value: fv.fmtLbl },
      { label: $t('@result.adv.pricing_model'), value: pricing },
      { label: $t('@result.adv.top_geos'), value: fv.topGeos, smallValue: true },
      { label: $t('@result.adv.cpa_range'), value: cpa },
      { label: $t('@result.adv.daily_spend'), value: daily },
      { label: $t('@result.adv.approval_time'), value: $t('@result.adv.approval_value') },
    ],
    highlightLabel: $t('@result.adv.highlight_label'),
    highlightValue: '$50',
    highlightUnit: $t('@result.adv.highlight_unit'),
    bonus,
  };
}

function sendToAccountManager() {
  if (submitting.value || !contactId.value.trim()) return;

  errors.clear();
  submitting.value = true;

  const payload = {
    role    : role.value,
    answers : state.value,
    contact : { network: contactNetwork.value, id: contactId.value.trim() },
    result  : { title: result.value.title, recommendations: result.value.cells },
  };

  return Quiz.sendLead(payload)
    .then((data)=> {
      if (data && data.success) return (sent.value = true);
      errors.add({ field: 'global', msg: (data && data.message) || $t('@ui.error_generic') });
    })
    .catch((rejection)=> {
      if (rejection && rejection.status === 422) return;
      errors.add({ field: 'global', msg: $t('@ui.error_network') });
    })
    .finally(()=> submitting.value = false);
}

if (props.start === 'advertiser' || props.start === 'publisher') {
  state.value = { role: props.start };
  currentFlow.value = (props.start === 'publisher' ? publisherFlow : advertiserFlow).slice(1);
  currentIdx.value = 0;
}

</script>

<template>
  <div class="quiz-shell" :data-role="state.role || ''">
    <div class="quiz-progress">
      <div class="progress-bar"><div class="progress-fill" :style="{ width: progressPct + '%' }"></div></div>
      <div class="progress-text">
        <span v-if="!isResult"><span>{{ stepNum }}</span> {{ $t('@ui.step_of') }} <span>{{ totalSteps }}</span></span>
        <span v-else>{{ $t('@ui.done') }}</span>
        <span>{{ $t('@ui.seconds_total') }}</span>
      </div>
    </div>

    <div v-if="!isResult && activeStep" class="quiz-step" :key="activeStep.name">
      <h2>{{ activeStep.title }}</h2>
      <p class="sub">{{ activeStep.sub }}</p>

      <div class="quiz-options" :class="{ compact: activeStep.compact }">
        <button
          v-for="opt in activeStep.options"
          :key="opt.value"
          type="button"
          class="quiz-option"
          :class="{ selected: state[activeStep.field] === opt.value }"
          @click="select(activeStep.field, opt.value)"
        >
          <span class="opt-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
          <span class="opt-body">
            <span class="opt-title">{{ opt.title }}</span>
            <span class="opt-sub">{{ opt.sub }}</span>
          </span>
        </button>
      </div>

      <div class="quiz-foot">
        <button type="button" class="quiz-back" :disabled="!historyStack.length" @click="back">{{ $t('@ui.back') }}</button>
        <span class="quiz-skip"><a :href="'/register' + '?role=' + role">{{ $t('@ui.skip') }}</a></span>
      </div>
    </div>

    <div v-else-if="isResult" class="quiz-step">
      <div class="result-pill">{{ $t('@ui.setup_ready') }}</div>
      <h2 class="result-title">{{ result.title }}</h2>

      <div class="reco-grid">
        <div v-for="cell in result.cells" :key="cell.label" class="reco-cell">
          <div class="reco-label">{{ cell.label }}</div>
          <div class="reco-value" :style="cell.smallValue ? 'font-size:14px' : ''">
            {{ cell.value }}<small v-if="cell.small">{{ cell.small }}</small>
          </div>
        </div>
      </div>

      <div class="reco-highlight">
        <div>
          <div class="reco-highlight-lbl">{{ result.highlightLabel }}</div>
          <div class="reco-highlight-num">{{ result.highlightValue }}<span>{{ result.highlightUnit }}</span></div>
        </div>
      </div>

      <div class="reco-bonus">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        <span>{{ result.bonus }}</span>
      </div>

      <div v-if="!sent" class="quiz-contact">
        <label class="quiz-contact-label">{{ $t('@ui.reach_you') }}</label>
        <div class="quiz-contact-row">
          <select v-model="contactNetwork" class="quiz-contact-select" :aria-label="$t('@ui.network_aria')">
            <option v-for="m in messengers" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
          <input v-model="contactId" type="text" class="quiz-contact-input" :class="{ 'has-error': errors.has('contact.id') }" @input="errors.remove('contact.id')" :placeholder="$t('@ui.contact_placeholder')" :aria-label="$t('@ui.contact_aria')">
        </div>
        <p v-if="err('contact.id')" class="quiz-error">{{ err('contact.id') }}</p>
        <p v-if="err('contact.network')" class="quiz-error">{{ err('contact.network') }}</p>
        <p v-if="err('global')" class="quiz-error">{{ err('global') }}</p>
      </div>

      <div v-if="sent" class="quiz-sent">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ $t('@ui.sent') }}
      </div>

      <div class="result-cta">
        <vue-ladda
          v-if="!sent"
          data-style="expand-right"
          class="btn btn-primary btn-result"
          :class="{ disabled: !contactId.trim() }"
          :loading="submitting"
          @click="sendToAccountManager"
        >
          {{ $t('@ui.send_cta') }}
        </vue-ladda>
        <a :href="'/quiz' + '?start=' + role" class="btn btn-ghost" style="width:100%;justify-content:center;">
          {{ $t('@ui.retake') }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>

.quiz-shell
  max-width: 620px
  margin: 0 auto
  padding: 40px 24px

.quiz-progress
  margin-bottom: 32px

.progress-bar
  height: 6px
  background: var(--line, #E5E7E2)
  border-radius: 999px
  overflow: hidden

.progress-fill
  height: 100%
  background: var(--brand-400, #3079c8)
  border-radius: 999px
  transition: width .35s ease

.quiz-shell[data-role="publisher"] .progress-fill
  background: var(--accent-400, #51c85d)

.progress-text
  display: flex
  justify-content: space-between
  font-size: 11px
  color: var(--muted, #6B7770)
  font-weight: 600
  text-transform: uppercase
  letter-spacing: .08em
  margin-top: 8px

.quiz-step
  animation: fadeIn .35s ease

@keyframes fadeIn
  from
    opacity: 0
    transform: translateY(8px)
  to
    opacity: 1
    transform: none

.quiz-step h2
  font-size: clamp(24px, 3.4vw, 32px)
  margin-bottom: 8px
  font-weight: 600

.quiz-step .sub
  color: var(--muted, #6B7770)
  font-size: 15px
  margin-bottom: 28px
  line-height: 1.55

.quiz-options
  display: grid
  gap: 10px
  margin-bottom: 24px

.quiz-options.compact .quiz-option
  padding: 14px 16px

.quiz-option
  display: flex
  align-items: center
  gap: 14px
  width: 100%
  background: var(--paper, #fff)
  border: 1px solid var(--line-2, #D7DBD3)
  border-radius: var(--r-md, 12px)
  padding: 16px 18px
  cursor: pointer
  text-align: left
  font-family: inherit
  transition: all .15s ease

  &:hover, &.selected
    border-color: var(--brand-400, #3079c8)
    background: var(--brand-50, #E8F1FB)

.quiz-shell[data-role="publisher"] .quiz-option:hover,
.quiz-shell[data-role="publisher"] .quiz-option.selected
  border-color: var(--accent-400, #51c85d)
  background: var(--accent-50, #EAF8EC)

.opt-icon
  width: 36px
  height: 36px
  border-radius: var(--r-sm, 8px)
  background: var(--bg, #FAFAF7)
  color: var(--brand-600, #20548B)
  display: grid
  place-items: center
  flex-shrink: 0

.quiz-option.selected .opt-icon
  background: var(--brand-400, #3079c8)
  color: #fff

.opt-body
  flex: 1

.opt-title
  font-family: var(--display, sans-serif)
  font-weight: 600
  font-size: 15px
  letter-spacing: -.01em
  margin-bottom: 2px
  display: block

.opt-sub
  font-size: 12px
  color: var(--muted, #6B7770)
  line-height: 1.45
  display: block

.quiz-foot
  margin-top: 24px
  padding-top: 20px
  border-top: 1px solid var(--line, #E5E7E2)
  display: flex
  justify-content: space-between
  align-items: center
  font-size: 13px

.quiz-back
  background: none
  border: none
  padding: 0
  color: var(--muted, #6B7770)
  font-weight: 600
  cursor: pointer
  font-family: inherit

  &:disabled
    opacity: .4
    cursor: not-allowed

.quiz-skip
  color: var(--muted, #6B7770)

  a
    color: var(--brand-500, #2966A8)
    font-weight: 600

.result-pill
  display: inline-block
  font-size: 12px
  font-weight: 700
  text-transform: uppercase
  letter-spacing: .06em
  color: var(--accent-600, #318C3B)
  background: var(--accent-50, #EAF8EC)
  border-radius: 999px
  padding: 5px 12px
  margin-bottom: 16px

.result-title
  font-size: clamp(22px, 3vw, 30px)
  font-weight: 600
  margin-bottom: 24px
  line-height: 1.25

.reco-grid
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 10px
  margin-bottom: 16px

.reco-cell
  background: var(--paper, #fff)
  border: 1px solid var(--line, #E5E7E2)
  border-radius: var(--r-sm, 8px)
  padding: 14px 16px

.reco-label
  font-size: 11px
  font-weight: 600
  text-transform: uppercase
  letter-spacing: .05em
  color: var(--muted, #6B7770)
  margin-bottom: 4px

.reco-value
  font-family: var(--display, sans-serif)
  font-size: 18px
  font-weight: 700
  color: var(--ink, #0A1612)

  small
    font-size: 11px
    font-weight: 500
    color: var(--muted, #6B7770)
    margin-left: 4px

.reco-highlight
  display: flex
  align-items: center
  justify-content: space-between
  gap: 16px
  background: var(--brand-900, #0F2A47)
  color: #fff
  border-radius: var(--r-md, 12px)
  padding: 18px 20px
  margin-bottom: 24px

.reco-highlight-lbl
  font-size: 12px
  text-transform: uppercase
  letter-spacing: .05em
  color: rgba(255,255,255,.7)
  margin-bottom: 4px

.reco-highlight-num
  font-family: var(--display, sans-serif)
  font-size: 26px
  font-weight: 900

  span
    font-size: 14px
    font-weight: 500
    color: rgba(255,255,255,.7)

.reco-bonus
  background: var(--accent-50, #EAF8EC)
  border: 1px solid var(--accent-100, #C9EECE)
  border-radius: var(--r-sm, 8px)
  padding: 14px 16px
  margin-bottom: 24px
  display: flex
  align-items: center
  gap: 12px
  font-size: 13px
  color: var(--accent-600, #318C3B)
  font-weight: 500
  line-height: 1.5

.quiz-contact
  margin-bottom: 24px

.quiz-contact-label
  display: block
  font-size: 13px
  font-weight: 600
  color: var(--ink-2, #1F2A26)
  margin-bottom: 8px

.quiz-contact-row
  display: grid
  grid-template-columns: minmax(120px, .5fr) 1fr
  gap: 10px

@media (max-width: 480px)
  .quiz-contact-row
    grid-template-columns: 1fr
  .reco-grid
    grid-template-columns: 1fr

.quiz-contact-select, .quiz-contact-input
  width: 100%
  padding: 12px 14px
  font: inherit
  font-size: 15px
  color: var(--ink, #0A1612)
  background: var(--paper, #fff)
  border: 1px solid var(--line-2, #D7DBD3)
  border-radius: var(--r-sm, 8px)

  &:focus
    outline: none
    border-color: var(--brand-400, #3079c8)
    box-shadow: 0 0 0 3px var(--brand-50, #E8F1FB)

.quiz-contact-input.has-error
  border-color: #dc2626

  &:focus
    border-color: #dc2626
    box-shadow: 0 0 0 3px #fee2e2

.quiz-error
  color: #dc2626
  font-size: 13px
  margin-top: 8px

.quiz-sent
  display: flex
  align-items: center
  gap: 10px
  background: var(--accent-50, #EAF8EC)
  border: 1px solid var(--accent-100, #C9EECE)
  color: var(--accent-600, #318C3B)
  border-radius: var(--r-sm, 8px)
  padding: 14px 16px
  margin-bottom: 24px
  font-size: 14px
  font-weight: 600

.result-cta
  display: flex
  gap: 10px
  flex-direction: column

.btn-result
  width: 100%
  justify-content: center
  padding: 16px
  font-size: 16px

  &:disabled
    opacity: .5
    cursor: not-allowed

</style>
